import React, { useState } from "react";
import { Icon } from "../UIElements/Icon";
import { Button } from "../UIElements/Button";
import Modal from "../UIElements/Modal";
import {
  useWalletTransactionsHistory,
  parseWalletEvent,
} from "../../../hooks/useWalletTransactionsHistory";
import { useGlobalUtils } from "../../../lib/globalUtils";
import "../../pages/customers/customer-pages.css";

const WalletTransactionHistoryModal = ({
  isOpen,
  onClose,
  wallet,
  userId,
  users = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { parsePrice, parseDate } = useGlobalUtils();

  const {
    data: responseData,
    isLoading,
    error,
    isError,
    refetch,
  } = useWalletTransactionsHistory(userId, wallet.id, {
    page: currentPage,
    pageSize,
    enabled: isOpen, // Only fetch when modal is open
  });

  const transactions = responseData?.transactions || [];
  const pagination = responseData?.pagination || {};

  const getWalletDisplayName = (type) => {
    switch (type) {
      case "cash":
        return "Cash Wallet";
      case "credit_point":
        return "Credit Points";
      default:
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const getWalletIcon = (type) => {
    switch (type) {
      case "cash":
        return "DollarSign";
      case "credit_point":
        return "Star";
      default:
        return "Wallet";
    }
  };

  const getWalletColor = (type) => {
    switch (type) {
      case "cash":
        return "text-success";
      case "credit_point":
        return "text-warning";
      default:
        return "text-primary";
    }
  };

  const formatAmount = (amount, walletType) => {
    if (walletType === "credit_point") {
      return `${Math.abs(amount)} pts`;
    }
    return parsePrice(Math.abs(amount));
  };

  const getAmountColor = (amount) => {
    return Math.sign(amount) === -1 ? "text-error" : "text-success";
  };

  const getAmountIcon = (amount) => {
    return Math.sign(amount) === -1 ? "ArrowDown" : "ArrowUp";
  };

  // Reset page when modal opens and refetch data
  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      // Refetch data when modal opens to get latest transactions
      refetch();
    }
  }, [isOpen, refetch]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Pagination helpers
  const totalPages = pagination.total_pages || 1;
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  const showPagination = totalPages > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Transaction History - ${getWalletDisplayName(wallet?.type)}`}
      subtitle={`View all transactions for this wallet`}
      maxWidth="max-w-4xl"
      className="ordering-customer-detail"
    >
      <div className="space-y-4">
        {/* Wallet Info Header */}
        <div className="border-border bg-card rounded-lg border p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-muted rounded-lg p-3">
              <Icon
                name={getWalletIcon(wallet?.type)}
                className={`h-6 w-6 ${getWalletColor(wallet?.type)}`}
              />
            </div>
            <div>
              <h4 className="text-foreground text-lg font-semibold">
                {getWalletDisplayName(wallet?.type)}
              </h4>
              <p className="text-muted-foreground text-sm">
                Current Balance:{" "}
                {wallet?.type === "credit_point"
                  ? `${wallet.balance} pts`
                  : parsePrice(wallet?.balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isLoading}
              className="bg-muted text-foreground hover:bg-accent flex items-center space-x-2 rounded-lg px-3 py-2 text-sm transition-colors disabled:opacity-50"
            >
              <Icon
                name="RefreshCw"
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              <span>{isLoading ? "Refreshing..." : "Refresh"}</span>
            </Button>

            {transactions.length > 0 && (
              <span className="text-muted-foreground text-sm">
                Page {currentPage} of {totalPages} •{" "}
                {pagination.total || transactions.length} total transactions
              </span>
            )}
          </div>

          {/* Auto-refresh indicator */}
          {isLoading && (
            <div className="text-primary flex items-center space-x-2 text-sm">
              <Icon name="Loader2" className="h-4 w-4 animate-spin" />
              <span>Updating...</span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-card rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-muted h-8 w-8 rounded-full"></div>
                      <div className="space-y-2">
                        <div className="bg-muted h-3 w-32 rounded"></div>
                        <div className="bg-muted h-2 w-24 rounded"></div>
                      </div>
                    </div>
                    <div className="bg-muted h-4 w-16 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="py-8 text-center">
            <Icon
              name="AlertCircle"
              className="text-error mx-auto mb-3 h-8 w-8"
            />
            <h3 className="text-foreground mb-1 text-sm font-medium">
              Error loading transactions
            </h3>
            <p className="text-muted-foreground mb-4 text-xs">
              {error?.message || "Failed to load transaction history"}
            </p>
            <Button
              variant="ghost"
              onClick={handleRefresh}
              className="bg-primary text-primary-foreground hover:bg-primary rounded-lg px-4 py-2 text-sm"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !isError &&
          (!transactions || transactions.length === 0) && (
            <div className="py-8 text-center">
              <Icon
                name="History"
                className="text-muted-foreground mx-auto mb-3 h-8 w-8"
              />
              <h3 className="text-foreground mb-1 text-sm font-medium">
                No transactions found
              </h3>
              <p className="text-muted-foreground text-xs">
                This wallet doesn&apos;t have any transaction history yet.
              </p>
            </div>
          )}

        {/* Transactions List */}
        {!isLoading && !isError && transactions && transactions.length > 0 && (
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const parsedEvent = parseWalletEvent(transaction, users);

              return (
                <div
                  key={transaction.id}
                  className="border-border bg-card hover:border-border rounded-lg border p-3 transition-colors sm:p-4"
                >
                  <div className="flex flex-col space-y-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
                    {/* Left side - Event info */}
                    <div className="flex min-w-0 flex-1 items-start space-x-3">
                      <div className={`bg-muted flex-shrink-0 rounded-lg p-2`}>
                        <Icon
                          name={parsedEvent.eventIcon}
                          className={`h-4 w-4 ${parsedEvent.eventColor}`}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        {/* Event description */}
                        <div className="mb-2 flex items-center space-x-2">
                          <p className="text-foreground text-sm font-medium break-words">
                            {parsedEvent.eventLog}
                          </p>
                        </div>

                        {/* Description */}
                        {parsedEvent.description && (
                          <p className="text-muted-foreground mb-2 text-sm break-words">
                            {parsedEvent.description}
                          </p>
                        )}

                        {/* Order information */}
                        {parsedEvent.orderId && (
                          <p className="text-primary mb-2 text-sm">
                            Order #{parsedEvent.orderId}
                          </p>
                        )}

                        {/* Transaction code */}
                        <div className="flex flex-col space-y-1">
                          <span className="text-muted-foreground text-xs">
                            Transaction Code:
                          </span>
                          <code className="bg-background text-muted-foreground w-fit overflow-hidden rounded px-2 py-1 font-mono text-xs break-all">
                            {transaction.code}
                          </code>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Amount and date */}
                    <div className="flex flex-row items-center justify-between space-x-3 sm:ml-4 sm:flex-shrink-0 sm:flex-col sm:items-end sm:justify-start sm:space-y-2 sm:space-x-0">
                      {/* Amount */}
                      <div
                        className={`flex items-center space-x-2 ${getAmountColor(transaction.amount)}`}
                      >
                        <Icon
                          name={getAmountIcon(transaction.amount)}
                          className="h-4 w-4"
                        />
                        <span className="text-base font-semibold sm:text-lg">
                          {formatAmount(transaction.amount, wallet.type)}
                        </span>
                      </div>

                      {/* Date */}
                      <span className="text-muted-foreground text-xs sm:text-sm">
                        {parseDate(transaction.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !isError && showPagination && (
          <div className="border-border flex flex-col items-center justify-between space-y-3 border-t pt-4 sm:flex-row sm:space-y-0">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
                className="bg-muted text-foreground hover:bg-accent flex items-center space-x-1 rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Icon name="ChevronLeft" className="h-4 w-4" />
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </Button>

              <span className="text-muted-foreground px-3 py-2 text-sm">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="ghost"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
                className="bg-muted text-foreground hover:bg-accent flex items-center space-x-1 rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">Next</span>
                <Icon name="ChevronRight" className="h-4 w-4" />
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-xs sm:text-right sm:text-sm">
              {transactions.length} transactions on this page
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WalletTransactionHistoryModal;
